import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, X, Send, Bot, User, Sparkles, 
  Loader2, CreditCard, Package, ChevronRight, Zap, Clock,
  ThumbsUp, ThumbsDown
} from 'lucide-react';
import { clientAPI } from '../../lib/api';
import { cn } from '../../lib/utils';

// Gradient border colors (Chatbase style)
const GRADIENT_COLORS = {
  from: '#f97316', // orange-500
  to: '#ec4899',   // pink-500
};

export default function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [credits, setCredits] = useState(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadCredits();
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadCredits = async () => {
    try {
      const response = await clientAPI.get('/ai/credits');
      setCredits(response.data);
    } catch (err) {
      console.error('Failed to load credits:', err);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await clientAPI.post('/ai/chat', {
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        actions: response.data.actions || [],
        creditsUsed: 1 // Each message costs 1 credit
      }]);

      // Update credits
      if (response.data.credits_remaining !== undefined) {
        setCredits(prev => prev ? { ...prev, total_available: response.data.credits_remaining } : null);
      }

    } catch (err) {
      console.error('Chat error:', err);
      if (err.response?.status === 402) {
        // Need credits
        setShowCreditsModal(true);
        setMessages(prev => [...prev, { 
          role: 'system', 
          content: 'You\'ve used all your credits! Get more to continue saving time with AI assistance.',
          isCreditsPrompt: true
        }]);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const purchaseCredits = async (packageId) => {
    try {
      const response = await clientAPI.post('/ai/credits/purchase', {
        package_id: packageId
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setError('Failed to start checkout. Please try again.');
    }
  };

  const submitFeedback = async (msgIndex, rating) => {
    const prev = feedback[msgIndex];
    const newRating = prev === rating ? null : rating;
    setFeedback(f => ({ ...f, [msgIndex]: newRating }));
    if (!newRating) return;
    try {
      await clientAPI.post('/ai/feedback', {
        session_id: sessionId,
        message_index: msgIndex,
        rating: newRating,
        is_public: false
      });
    } catch (err) {
      console.error('Feedback error:', err);
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full shadow-lg",
          "flex items-center justify-center",
          "bg-gradient-to-r from-orange-500 to-pink-500",
          "hover:shadow-xl hover:scale-105 transition-all"
        )}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="ai-chat-toggle"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-36 right-6 z-50 w-[400px] h-[550px] max-h-[70vh] rounded-2xl shadow-2xl overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${GRADIENT_COLORS.from}, ${GRADIENT_COLORS.to})`,
              padding: '2px',
            }}
          >
            <div className="w-full h-full bg-white rounded-[14px] flex flex-col overflow-hidden">
              {/* Header with Credit Balance */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#EBEBEB] bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A] text-sm">AI Assistant</h3>
                    <p className="text-xs text-[#525252]">Save time with AI</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Credit Balance Badge */}
                  <button
                    onClick={() => setShowCreditsModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-full hover:border-orange-300 transition-colors"
                  >
                    <Zap className="w-3.5 h-3.5 text-orange-500" />
                    <span className="text-xs font-semibold text-orange-600">
                      {credits?.total_available ?? '...'} credits
                    </span>
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-lg hover:bg-[#F5F5F5] transition-colors"
                  >
                    <X className="w-4 h-4 text-[#525252]" />
                  </button>
                </div>
              </div>

              {/* Time Saving Banner */}
              <div className="px-4 py-2 bg-gradient-to-r from-orange-50 to-pink-50 border-b border-orange-100">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <p className="text-xs text-orange-700">
                    <span className="font-medium">Save up to 50% of your time</span> — 1 credit per response
                  </p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FAFAFA]">
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-orange-500/10 to-pink-500/10 flex items-center justify-center mx-auto mb-4">
                      <Bot className="w-8 h-8 text-orange-500" />
                    </div>
                    <h4 className="font-semibold text-[#0A0A0A] mb-2">How can I help you?</h4>
                    <p className="text-sm text-[#525252] max-w-[280px] mx-auto">
                      Ask me about our services, pricing, or anything else. I'll help you find what you need faster!
                    </p>
                  </div>
                )}

                {messages.map((msg, idx) => (
                  <div key={idx}>
                    {msg.isCreditsPrompt ? (
                      // Credits prompt message
                      <div className="bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-xl p-4 text-center">
                        <Zap className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                        <p className="text-sm text-orange-700 font-medium mb-3">{msg.content}</p>
                        <button
                          onClick={() => setShowCreditsModal(true)}
                          className="px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-medium rounded-lg hover:shadow-md transition-all"
                        >
                          Get More Credits
                        </button>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          "flex gap-3",
                          msg.role === 'user' ? 'justify-end' : 'justify-start'
                        )}
                      >
                        {msg.role === 'assistant' && (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div className="flex flex-col max-w-[280px]">
                          <div
                            className={cn(
                              "rounded-2xl px-4 py-3 text-sm",
                              msg.role === 'user'
                                ? 'bg-[#0A0A0A] text-white rounded-br-md'
                                : 'bg-white border border-[#EBEBEB] text-[#0A0A0A] rounded-bl-md shadow-sm'
                            )}
                          >
                            {msg.content}
                          </div>
                          {/* Credits used + feedback for assistant messages */}
                          {msg.role === 'assistant' && (
                            <div className="flex items-center gap-2 mt-1 ml-1">
                              {msg.creditsUsed && (
                                <div className="flex items-center gap-1">
                                  <Zap className="w-3 h-3 text-orange-400" />
                                  <span className="text-[10px] text-[#A3A3A3]">
                                    {msg.creditsUsed} credit
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center gap-0.5 ml-auto">
                                <button
                                  onClick={() => submitFeedback(idx, 'up')}
                                  className={cn(
                                    "p-1 rounded transition-colors",
                                    feedback[idx] === 'up'
                                      ? "text-green-500"
                                      : "text-[#C4C4C4] hover:text-green-400"
                                  )}
                                  data-testid={`feedback-up-${idx}`}
                                >
                                  <ThumbsUp className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => submitFeedback(idx, 'down')}
                                  className={cn(
                                    "p-1 rounded transition-colors",
                                    feedback[idx] === 'down'
                                      ? "text-red-400"
                                      : "text-[#C4C4C4] hover:text-red-300"
                                  )}
                                  data-testid={`feedback-down-${idx}`}
                                >
                                  <ThumbsDown className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        {msg.role === 'user' && (
                          <div className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-[#525252]" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white border border-[#EBEBEB] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                        <span className="text-sm text-[#525252]">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-[#EBEBEB] bg-white">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message..."
                    className="flex-1 h-10 px-4 text-sm bg-[#F5F5F5] border-0 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                    disabled={isLoading}
                    data-testid="ai-chat-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || isLoading}
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                      input.trim() && !isLoading
                        ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:shadow-md"
                        : "bg-[#F5F5F5] text-[#A3A3A3]"
                    )}
                    data-testid="ai-chat-send"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Credits Modal */}
      <AnimatePresence>
        {showCreditsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowCreditsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A]">Get AI Credits</h3>
                    <p className="text-xs text-[#525252]">Save time with instant AI assistance</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreditsModal(false)}
                  className="p-2 rounded-lg hover:bg-[#F5F5F5]"
                >
                  <X className="w-4 h-4 text-[#525252]" />
                </button>
              </div>

              {/* Current Balance */}
              {credits && (
                <div className="mb-4 p-3 bg-gradient-to-r from-orange-50 to-pink-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#525252]">Your balance</span>
                    <div className="flex items-center gap-1">
                      <Zap className="w-4 h-4 text-orange-500" />
                      <span className="font-bold text-orange-600">{credits.total_available} credits</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {credits?.packages?.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => purchaseCredits(pkg.id)}
                    className={cn(
                      "w-full p-4 rounded-xl border-2 text-left transition-all hover:border-orange-500 hover:shadow-md",
                      pkg.popular ? "border-orange-500 bg-orange-50" : "border-[#EBEBEB]"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          pkg.popular ? "bg-orange-500 text-white" : "bg-[#F5F5F5] text-[#525252]"
                        )}>
                          <Package className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[#0A0A0A]">{pkg.name}</span>
                            {pkg.popular && (
                              <span className="text-[10px] px-2 py-0.5 bg-orange-500 text-white rounded-full font-medium">
                                BEST VALUE
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#525252]">{pkg.credits} AI responses</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-[#0A0A0A]">${pkg.price}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-4 text-center">
                <p className="text-xs text-[#A3A3A3]">
                  Credits never expire • Secure payment via Stripe
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
